/* =============================================================================
 * M.M.LaFleur — slide-out cart.
 *
 * Opens on any link to /cart, on quantity changes, and after an add to cart.
 *
 * Things worth knowing about the approach:
 *
 *   1. Nothing here edits mm-editorial.js. Add-to-cart already lives there and
 *      posts to /cart/add.js; rather than rewrite a 21KB file to fire an event,
 *      this wraps window.fetch and watches for those posts. Contained, and it
 *      also catches any other code path that adds to the cart.
 *
 *   2. Only the contents region is re-rendered, by fetching
 *      /cart?view=mm-drawer. The checkout form and Shopify's express payment
 *      buttons are left alone — they bind listeners on page load and replacing
 *      their markup leaves dead buttons.
 *
 *   3. Line items are addressed by line number, not variant id, so a cart
 *      holding the same variant twice (different properties) still updates the
 *      right row.
 *
 *   4. Clicks are handled in the CAPTURE phase, not the bubble phase. The theme
 *      runs Barba page transitions, which binds its own bubble-phase click
 *      listener on document to intercept links. A bubble-phase preventDefault
 *      here comes too late: Barba has already committed to navigating, so
 *      opening the drawer from the bag icon ALSO soft-navigated to /cart
 *      underneath it, and closing the drawer revealed the cart page instead of
 *      the page you were on. Capturing on document runs before any bubble-phase
 *      listener, and stopPropagation there keps the event from ever reaching
 *      Barba. Propagation is only stopped for clicks this file actually
 *      handles, so other delegated listeners are unaffected.
 *
 *   5. ONE DRAWER ONLY (v4.6.9, Sept 22 2026). Platter's own Cart Drawer
 *      (section group 0_modal.json, modal id "modal--cart-drawer") is still
 *      enabled and still carries Platter demo content — a "$2,000 from Free
 *      Shipping" bar, gift-with-purchase and upsell blocks pointing at demo
 *      products, a coupon field. Platter's add-to-cart and header code open it
 *      by setting the Alpine modal store to that id, so on some paths BOTH
 *      drawers opened and closing one revealed the other.
 *
 *      Rather than rewrite the 59KB section group through the Admin API, this
 *      file owns the outcome: any attempt to open Platter's cart modal is
 *      cleared on the next tick and this drawer opens instead, links to
 *      #modal--cart-drawer are intercepted in the capture phase, and the
 *      Platter modal is hidden with an injected rule so it cannot flash for
 *      that one tick. The clean long-term fix is to disable the "Cart Drawer"
 *      section in the theme editor (Modals group) — this guard stays harmless
 *      once that is done. Pre-change copy: assets/mm-cart.pre-soledrawer-backup.js
 *
 *   6. FOCUS AND ANNOUNCEMENTS AFTER A QUANTITY CHANGE. Re-rendering the body
 *      with innerHTML destroys the button the visitor just pressed, and focus
 *      falls back to <body> — OUTSIDE the modal dialog, so the next Tab lands
 *      somewhere on the page behind it and a screen reader says nothing at
 *      all about what changed. Before each change the line's cart key (stable
 *      across re-renders, unlike its line number) and which control was used
 *      (data-mm-cart-ctl: dec / inc / remove) are remembered; afterwards focus
 *      goes back to the same control on the same line, or to the close button
 *      when the line is gone. A polite status region inside the panel then
 *      says what happened ("Quantity updated to 2. Subtotal $329.00").
 * ========================================================================== */

(function () {
  "use strict";

  var root = null;
  var panel = null;
  var body = null;
  var lastFocus = null;
  var busy = false;
  var status = null;

  var PLATTER_CART_ID = /cart-drawer/i;

  function el(sel) { return root ? root.querySelector(sel) : null; }

  function init() {
    root = document.querySelector("[data-mm-cart]");
    if (!root) return false;
    panel = el(".mm-cart-panel");
    body = el("[data-mm-cart-body]");
    ensureStatus();
    return true;
  }

  /* The live region has to be in the DOM BEFORE its text changes or most
     screen readers ignore the first message, so it is created up front. It
     sits in the panel rather than the body because the body is replaced on
     every render, and inside the dialog because aria-modal hides everything
     outside it. */
  function ensureStatus() {
    if (!panel) return;
    status = panel.querySelector("[data-mm-cart-status]");
    if (status) return;
    status = document.createElement("div");
    status.className = "mm-sr";
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");
    status.setAttribute("data-mm-cart-status", "");
    panel.appendChild(status);
  }

  function announce(message) {
    if (!status) return;
    /* Clear first, then set on a later tick, so an identical message (two
       presses of + that both fail at the stock limit) is still re-read. */
    status.textContent = "";
    window.setTimeout(function () { status.textContent = message; }, 100);
  }

  function plainText(html) {
    var d = document.createElement("div");
    d.innerHTML = html || "";
    return (d.textContent || "").trim();
  }

  /* --- open / close ------------------------------------------------------ */

  function open() {
    if (!root && !init()) return;
    if (root.getAttribute("data-open") === "true") return;
    lastFocus = document.activeElement;
    root.hidden = false;
    /* Next frame, so the panel has a rendered starting transform to slide from
       rather than appearing already in place. */
    window.requestAnimationFrame(function () {
      root.setAttribute("data-open", "true");
    });
    document.body.classList.add("mm-no-scroll");
    var close = el(".mm-cart-close");
    if (close) close.focus();
  }

  function close() {
    if (!root || root.hidden) return;
    root.setAttribute("data-open", "false");
    document.body.classList.remove("mm-no-scroll");
    window.setTimeout(function () {
      if (root.getAttribute("data-open") === "false") root.hidden = true;
    }, 400);
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
    lastFocus = null;
  }

  function isOpen() {
    return root && !root.hidden && root.getAttribute("data-open") === "true";
  }

  /* --- rendering -------------------------------------------------------- */

  function applyState() {
    var node = body.querySelector("[data-mm-cart-state]");
    if (!node) return null;
    var state;
    try {
      state = JSON.parse(node.textContent);
    } catch (err) {
      return null;
    }

    var count = el("[data-mm-cart-count]");
    if (count) count.textContent = String(state.count);

    var subtotal = el("[data-mm-cart-subtotal]");
    if (subtotal) subtotal.innerHTML = state.subtotalText;

    panel.setAttribute("data-empty", state.empty ? "true" : "false");

    /* Keep the header bag count in sync with the drawer. The header markup
       predates this drawer, so match generously rather than assume one hook. */
    var badges = document.querySelectorAll("[data-mm-cart-count-badge], .mm-bag-count");
    Array.prototype.forEach.call(badges, function (b) {
      b.textContent = String(state.count);
    });
    return state;
  }

  function refresh(after) {
    return fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + "cart?view=mm-drawer"
        : "/cart?view=mm-drawer", { credentials: "same-origin" })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        body.innerHTML = html;
        var state = applyState();
        if (after) after(state);
      })
      .catch(function () { /* leave the drawer as-is rather than blanking it */ });
  }

  function openFresh() {
    if (!root && !init()) return;
    open();
    refresh();
  }

  /* --- mutations -------------------------------------------------------- */

  /* Put focus back where the visitor was and say what changed. `from` is
     the remembered line key, control and quantity (see note 6). */
  function restore(from, state) {
    var subtotal = state ? plainText(state.subtotalText) : "";
    var tail = subtotal ? " Subtotal " + subtotal + "." : "";

    var row = null;
    if (from.key) {
      Array.prototype.some.call(body.querySelectorAll("[data-mm-cart-key]"), function (r) {
        if (r.getAttribute("data-mm-cart-key") === from.key) { row = r; return true; }
        return false;
      });
    }

    if (!row) {
      var closeBtn = el(".mm-cart-close");
      if (closeBtn) closeBtn.focus();
      if (state && state.empty) {
        announce((from.name ? from.name + " removed. " : "Item removed. ") + "Your bag is empty.");
      } else {
        announce((from.name ? from.name + " removed." : "Item removed.") + tail);
      }
      return;
    }

    var target = row.querySelector('[data-mm-cart-ctl="' + from.ctl + '"]');
    if (target) target.focus();

    var now = parseInt(row.getAttribute("data-mm-cart-qty-now"), 10);
    if (now === from.qty) {
      /* /cart/change.js refuses quantities over available stock and the line
         stays put — say so rather than claim an update that did not happen. */
      announce("Quantity unchanged at " + now + "." + tail);
    } else {
      announce("Quantity updated to " + now + "." + tail);
    }
  }

  function changeLine(line, quantity, from) {
    if (busy) return;
    busy = true;
    panel.setAttribute("data-busy", "true");
    fetch("/cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function () {
        return refresh(from ? function (state) { restore(from, state); } : null);
      })
      .finally(function () {
        busy = false;
        panel.setAttribute("data-busy", "false");
      });
  }

  /* --- wiring ----------------------------------------------------------- */

  function handled(e) {
    e.preventDefault();
    /* Capture phase: this keeps the click from ever reaching Barba's
       bubble-phase link handler, which would otherwise navigate. */
    e.stopPropagation();
  }

  document.addEventListener("click", function (e) {
    if (!root && !init()) return;
    if (!e.target || !e.target.closest) return;

    if (e.target.closest("[data-mm-cart-close]")) {
      handled(e);
      close();
      return;
    }

    var qty = e.target.closest("[data-mm-cart-qty]");
    if (qty) {
      handled(e);
      var row = qty.closest("[data-mm-cart-line]");
      if (!row) return;
      changeLine(
        parseInt(row.getAttribute("data-mm-cart-line"), 10),
        parseInt(qty.getAttribute("data-mm-cart-qty"), 10),
        {
          key: row.getAttribute("data-mm-cart-key") || "",
          name: row.getAttribute("data-mm-cart-name") || "",
          ctl: qty.getAttribute("data-mm-cart-ctl") || "inc",
          qty: parseInt(row.getAttribute("data-mm-cart-qty-now"), 10)
        }
      );
      return;
    }

    /* Platter's cart button links to /#modal--cart-drawer and binds an onclick
       that opens Platter's drawer. Capturing here on document stops the event
       before it reaches that link, so only this drawer opens. */
    if (e.target.closest('a[href*="#modal--cart-drawer"]') && e.button === 0) {
      handled(e);
      openFresh();
      return;
    }

    /* Any link to the cart opens the drawer instead of navigating. Modified
       clicks fall through untouched, so cmd-click still opens the cart page in
       a new tab and the drawer keeps a working no-JS fallback. */
    var cartLink = e.target.closest('a[href$="/cart"], a[href*="/cart?"]');
    if (cartLink && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
      handled(e);
      openFresh();
    }
  }, true);

  document.addEventListener("keydown", function (e) {
    if (!isOpen()) return;
    if (e.key === "Escape") { e.preventDefault(); close(); return; }
    if (e.key !== "Tab") return;

    var stops = panel.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    stops = Array.prototype.filter.call(stops, function (n) { return n.offsetParent !== null; });
    if (!stops.length) return;
    var first = stops[0];
    var last = stops[stops.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* Catch adds to cart wherever they come from. mm-editorial.js posts to
     /cart/add.js from the PDP and the quick-add modal; rather than couple to
     either, watch the network call itself. */
  var nativeFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var url = args[0];
    if (url && url.url) url = url.url;
    var isAdd = typeof url === "string" && url.indexOf("/cart/add") !== -1;

    return nativeFetch.apply(this, args).then(function (response) {
      if (isAdd && response.ok) {
        if (root || init()) {
          refresh().then(open);
        }
      }
      return response;
    });
  };

  /* --- one drawer only (see note 5) ------------------------------------- */

  /* Belt: hide Platter's cart modal outright so it can never paint, even for
     the single tick before the store watcher below clears it. */
  (function hidePlatterDrawer() {
    if (document.querySelector("style[data-mm-sole-drawer]")) return;
    var s = document.createElement("style");
    s.setAttribute("data-mm-sole-drawer", "1");
    s.textContent = '[data-megamenu="modal--cart-drawer"]{display:none !important;}';
    (document.head || document.documentElement).appendChild(s);
  }());

  /* Braces: watch Platter's Alpine modal store. Whenever anything asks it to
     open the cart drawer, clear that request and open this drawer instead.
     Clearing it also releases the body scroll lock Platter applies for open
     modals. */
  function watchPlatterCart() {
    if (window.__mmPlatterCartWatched) return true;
    var A = window.Alpine;
    if (!A || typeof A.store !== "function" || typeof A.effect !== "function") return false;
    var store = A.store("modal");
    if (!store) return false;
    window.__mmPlatterCartWatched = true;

    A.effect(function () {
      var id = store.id;
      if (!id || !PLATTER_CART_ID.test(String(id))) return;
      window.setTimeout(function () {
        if (PLATTER_CART_ID.test(String(store.id))) store.setId("");
        openFresh();
      }, 0);
    });
    return true;
  }

  if (!watchPlatterCart()) {
    document.addEventListener("alpine:initialized", watchPlatterCart, { once: true });
    var tries = 0;
    var poll = window.setInterval(function () {
      tries += 1;
      if (watchPlatterCart() || tries > 40) window.clearInterval(poll);
    }, 250);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
}());
